import Button from 'common/components/button/Button';
import ScreenContainer from 'common/components/screenContainer/ScreenContainer';
import ScreenHeader from 'common/components/screenHeader/ScreenHeader';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './GodModesScreen.module.scss';

// Task #66078: single entry point for the platform-admin god-mode actions, so admins
// don't have to memorize each hidden URL. Each "Aplicar" opens the action in a new tab.
interface GodModeItem {
    key: string;
    url: string;
    needsCompanyId?: boolean;
}

const GOD_MODES: GodModeItem[] = [
    { key: 'prefill_capacity_from_gps', url: '/god-mode/prefill-capacity-from-gps' },
    { key: 'update_geofences', url: '/god-mode/updateGeofencesToTrackingDB' },
    { key: 'update_segments_images', url: '/god-mode/updateSegmentsImages' },
    { key: 'run_address_hydration', url: '/god-mode/run-address-hydration' },
    { key: 'calculate_all_monthly_costs', url: '/god-mode/calculate-all-monthly-costs' },
    { key: 'calculate_monthly_costs', url: '/god-mode/calculate-monthly-costs', needsCompanyId: true },
];

const GodModesScreen: React.FC = () => {
    const { t } = useTranslation();
    const [companyIds, setCompanyIds] = useState<Record<string, string>>({});

    // Access control is enforced by the PolicyRoute (SETTINGUP_COMPANIES_WRITE = platform admin)
    // and by each action's server-side policy. Render unconditionally so the page never blanks
    // out while the profile is still hydrating in a freshly-opened tab.
    const apply = (item: GodModeItem) => {
        const companyId = (companyIds[item.key] ?? '').trim();
        const url = item.needsCompanyId ? `${item.url}/${companyId}` : item.url;
        window.open(url, '_blank');
    };

    return (
        <ScreenTitle title={t('god_modes.title') as string}>
            <ScreenContainer>
                <ScreenHeader title={t('god_modes.title') as string} />
                <p className={styles.subtitle}>{t('god_modes.subtitle') as string}</p>
                <div className={styles.list}>
                    {GOD_MODES.map(item => {
                        const needsId = Boolean(item.needsCompanyId);
                        const idValue = companyIds[item.key] ?? '';
                        return (
                            <div key={item.key} className={styles.card}>
                                <div className={styles.info}>
                                    <span className={styles.cardTitle}>{t(`god_modes.items.${item.key}.title` as any) as string}</span>
                                    <span className={styles.cardDescription}>{t(`god_modes.items.${item.key}.description` as any) as string}</span>
                                </div>
                                <div className={styles.actions}>
                                    {needsId &&
                                        <input
                                            className={styles.input}
                                            value={idValue}
                                            placeholder={t('god_modes.company_id_placeholder') as string}
                                            onChange={e => setCompanyIds(prev => ({ ...prev, [item.key]: e.target.value }))}
                                        />
                                    }
                                    <Button
                                        text={t('god_modes.apply') as string}
                                        preset="primary"
                                        isDisabled={needsId && !idValue.trim()}
                                        onClick={() => apply(item)}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </ScreenContainer>
        </ScreenTitle>
    );
};

export default GodModesScreen;
